import { randomUUID } from "node:crypto";
import { TableClient, type TransactionAction } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import { CURSOR_TABLE_NAME } from "@m8t-stack/brain-engine";

/**
 * One atomic transaction proves the operator can perform the SAME Insert-or-
 * Replace operation that the final dream cursor advance needs without leaving
 * a row behind. Its second operation is a deliberately impossible conditional
 * update; Azure rejects it only after authorizing the whole transaction and
 * rolls back the first upsert atomically.
 */
const PROBE_PARTITION_PREFIX = "__m8t_dream_cursor_write_preflight__";
const NONMATCHING_TABLE_ETAG = "W/\"datetime'1970-01-01T00%3A00%3A00.0000000Z'\"";

export interface DreamCursorWriteProbeClient {
  createTable(): Promise<unknown>;
  submitTransaction(actions: TransactionAction[]): Promise<unknown>;
}

interface TableServiceError {
  statusCode?: number;
  code?: string;
  message?: string;
  details?: { errorCode?: string };
}

function serviceCode(error: unknown): {
  statusCode: number | undefined;
  code: string | undefined;
  message: string | undefined;
} {
  const candidate = error as TableServiceError;
  return {
    statusCode: candidate.statusCode,
    code: candidate.code ?? candidate.details?.errorCode,
    message: candidate.message,
  };
}

function isTableAlreadyExists(error: unknown): boolean {
  const { statusCode, code } = serviceCode(error);
  return statusCode === 409 && code === "TableAlreadyExists";
}

function provesAuthorizedNonMutation(error: unknown): boolean {
  const { statusCode, code, message } = serviceCode(error);
  // Azure's transaction contract prefixes entity-specific failures with the
  // zero-based failed operation index. Only operation 1 is our deliberate
  // abort; accepting an operation-0 failure could misread a rejected upsert as
  // proof that the exact cursor write was authorized.
  if (!message?.startsWith("1:")) return false;
  return (
    (statusCode === 404 && (code === "EntityNotFound" || code === "ResourceNotFound")) ||
    (statusCode === 412 && code === "UpdateConditionNotSatisfied")
  );
}

/**
 * Prove the BrainDreamCursor table and entity write are authorized before a
 * manual live dream can reach its model or Git write. The probe upsert is
 * always rolled back with the transaction; no entity is retained.
 */
export async function preflightDreamCursorWrite(
  client: DreamCursorWriteProbeClient,
  uuid: () => string = randomUUID,
): Promise<void> {
  try {
    await client.createTable();
  } catch (error) {
    if (!isTableAlreadyExists(error)) throw error;
  }

  const attempt = uuid();
  const partitionKey = `${PROBE_PARTITION_PREFIX}-${attempt}`;
  const actions: TransactionAction[] = [
    [
      "upsert",
      { partitionKey, rowKey: `upsert-${attempt}`, proof: "authorization-only" },
      "Replace",
    ],
    [
      "update",
      { partitionKey, rowKey: `abort-${attempt}`, proof: "atomic-rollback" },
      "Merge",
      { etag: NONMATCHING_TABLE_ETAG },
    ],
  ];
  try {
    await client.submitTransaction(actions);
  } catch (error) {
    if (provesAuthorizedNonMutation(error)) return;
    throw error;
  }

  throw new Error("Dream cursor write preflight transaction unexpectedly committed.");
}

export async function preflightDreamCursorWriteAt(
  tableEndpoint: string,
  credential: TokenCredential,
): Promise<void> {
  const client = new TableClient(tableEndpoint, CURSOR_TABLE_NAME, credential);
  await preflightDreamCursorWrite(client);
}
